import Box from '@mui/material/Box';
import type { BaseRow } from '@openx/types/tableHelpers';
import { STICKY_POSITION } from '@openx/types/tableHelpers';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DealHealthScore,
  type DealHealthScoreValue,
} from '../../../../../../components/DealHealthScore/DealHealthScore';
import { DEAL_INSIGHTS_COLUMN_MIN_WIDTH } from '../../../../HeatTable/constants';
import { useHeatmapTableColumns } from '../../../../HeatTable/hooks/useHeatmapTableColumns';
import type { ColorConfig, ColumnConfig } from '../../../../HeatTable/types';
import { NameCell } from '../parts/NameCell';

export type DealInsightsData = BaseRow & {
  deal_name: string;
  deal_id: string;
  ox_deal_id: number;
  health_score: DealHealthScoreValue;
  impressions: string;
  impressions_raw: number | null;
  spend: string;
  spend_raw: number | null;
  vcr: string;
  vcr_raw: number | null;
  ctr: string;
  ctr_raw: number | null;
  bid_rate: string;
  bid_rate_raw: number | null;
  ecpm: string;
  ecpm_raw: number | null;
  viewability: string;
  viewability_raw: number | null;
  win_rate: string;
  win_rate_raw: number | null;
  iqs_on_impression: string;
  iqs_on_impression_raw: number | null;
};

export type MaxValuesConfig = {
  impressions: ColorConfig<DealInsightsData>;
  spend: ColorConfig<DealInsightsData>;
  ecpm: ColorConfig<DealInsightsData>;
  bid_rate: ColorConfig<DealInsightsData>;
  win_rate: ColorConfig<DealInsightsData>;
  viewability: ColorConfig<DealInsightsData>;
  vcr: ColorConfig<DealInsightsData>;
  ctr: ColorConfig<DealInsightsData>;
  iqs_on_impression: ColorConfig<DealInsightsData>;
};

type HealthScoreRootCauseActions = {
  onLoadRootCause?: (dealId: string) => void;
  onReloadRootCause?: (dealId: string) => void;
  showCurrentState?: boolean;
};

const createColumn = (
  key: keyof DealInsightsData,
  titleKey: string,
  maxValue: ColorConfig<DealInsightsData>,
  t: (key: string) => string,
  options: { prefix?: string; suffix?: string; tooltipMessage?: string } = {}
): ColumnConfig<DealInsightsData> => ({
  key,
  title: t(titleKey),
  width: '5%',
  minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
  ...(options.prefix && { prefix: options.prefix }),
  ...(options.suffix && { suffix: options.suffix }),
  ...(options.tooltipMessage && { tooltipMessage: options.tooltipMessage }),
  colorConfig: {
    ...maxValue,
    valueKey: `${key}_raw` as keyof DealInsightsData,
  },
  headerStyle: {
    '& > div': {
      justifyContent: 'center',
    },
  },
});

const createColumnsConfig = (
  maxValues: MaxValuesConfig,
  t: (key: string) => string,
  actions: HealthScoreRootCauseActions
): ColumnConfig<DealInsightsData>[] => [
  {
    key: 'deal_name',
    title: t('Deal Name'),
    width: '25%',
    minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.DEAL_NAME,
    sticky: STICKY_POSITION.LEFT,
    disabled: true,
    freeze: true,
    render: ({ deal_name, ox_deal_id, deal_id }) => (
      <NameCell id={deal_id} name={deal_name} pathTo={`/deals/${ox_deal_id}/details`} subRowLabel={t('Deal ID:')} />
    ),
    cellStyle: {
      alignItems: 'flex-start',
      fontWeight: 'fontWeightBold',
      paddingLeft: 0,
      whiteSpace: 'pre',
    },
    headerStyle: {
      paddingLeft: 4,
    },
  },
  {
    key: 'health_score',
    title: t('Health Score'),
    tooltipMessage: t(
      "Averaged across the selected range deal's operational health score, based on its full funnel performance (current and historical), sustained trends, and the performance of deals with a similar setup."
    ),
    width: '160px',
    minWidth: '160px',

    render: ({ health_score, ox_deal_id }) => (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <DealHealthScore
          maxWidth={120}
          onOpen={() => actions.onLoadRootCause?.(String(ox_deal_id))}
          onReload={() => actions.onReloadRootCause?.(String(ox_deal_id))}
          showCurrentState={actions.showCurrentState}
          value={health_score}
        />
      </Box>
    ),
    headerStyle: {
      '& > div': {
        justifyContent: 'center',
      },
    },
  } satisfies ColumnConfig<DealInsightsData>,
  createColumn('impressions', 'Impressions', maxValues.impressions, t),
  createColumn('spend', 'Spend', maxValues.spend, t, { prefix: '$' }),
  createColumn('ecpm', 'eCPM', maxValues.ecpm, t, { prefix: '$' }),
  createColumn('bid_rate', 'Bid Rate', maxValues.bid_rate, t, { suffix: '%' }),
  createColumn('win_rate', 'Win Rate', maxValues.win_rate, t, { suffix: '%' }),
  createColumn('viewability', 'Viewability', maxValues.viewability, t, { suffix: '%' }),
  createColumn('vcr', 'VCR', maxValues.vcr, t, { suffix: '%' }),
  createColumn('ctr', 'CTR', maxValues.ctr, t, { suffix: '%' }),
  {
    ...createColumn('iqs_on_impression', 'Inventory Quality', maxValues.iqs_on_impression, t, {
      tooltipMessage: t(
        'Average Inventory Quality Score (0-100) across scored domains. Displayed only when at least 80% of impressions come from scored domains and filters are not applied.'
      ),
    }),
    width: '180px',
    minWidth: '180px',
  },
];

export const useGeneralDataTableColumns = (maxValues: MaxValuesConfig, actions: HealthScoreRootCauseActions = {}) => {
  const { t } = useTranslation();

  const columnsConfig = useMemo(() => createColumnsConfig(maxValues, t, actions), [actions, maxValues, t]);

  return useHeatmapTableColumns(columnsConfig);
};
